import { Injectable, NgZone, signal } from '@angular/core';
import {
  ConflictResolutionMessage,
  DEFAULT_PROJECT_NAME,
  ResponseProject,
  ResponseProjectProcessingMessage,
  ResponseProjectProcessingPayload,
} from '../models/response-project';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { DocumentProcessingStepEnum, isArchiveFile, ResponseProjectUploadFile } from '../models/response-project-document';

export interface WsConnection {
  connectionState$: Observable<'idle' | 'connecting' | 'open' | 'closed' | 'error'>;
  messages$: Observable<ResponseProjectProcessingMessage>;
  sendPayload(payload: ResponseProjectProcessingPayload): void;
  sendConflictResolution(message: ConflictResolutionMessage): void;
  disconnect(): void;
}

export interface UploadScopeState {
  files: ResponseProjectUploadFile[];
  uploadCompleted: number;
  uploadTotal: number;
  categorizationCompleted: number;
  categorizationTotal: number;
}

@Injectable({
  providedIn: 'root',
})
export class TendersResponsesService {
  projects = signal<ResponseProject[]>([]);
  addinInstalled = signal<boolean>(false);
  newProjectId = signal<string | null>(null);

  readonly uploadStates = signal<Record<string, UploadScopeState>>({});
  private readonly processingConns = new Map<string, Set<WsConnection>>();

  constructor(private ngZone: NgZone) {}

  hasActiveProcessing(projectId: string, scope: string): boolean {
    return (this.processingConns.get(this.fileKey(projectId, scope))?.size ?? 0) > 0;
  }

  fileKey(projectId: string, scope: string): string {
    return `${projectId}:${scope}`;
  }

  private emptyScope(): UploadScopeState {
    return {
      files: [],
      uploadCompleted: 0,
      uploadTotal: 0,
      categorizationCompleted: 0,
      categorizationTotal: 0,
    };
  }

  setFiles(projectId: string, scope: string, files: ResponseProjectUploadFile[]): void {
    const key = this.fileKey(projectId, scope);
    this.uploadStates.update(all => {
      const prev = all[key] ?? this.emptyScope();
      return { ...all, [key]: { ...prev, files: [...files] } };
    });
  }

  appendFile(projectId: string, scope: string, file: ResponseProjectUploadFile): void {
    const key = this.fileKey(projectId, scope);
    this.uploadStates.update(all => {
      const prev = all[key] ?? this.emptyScope();
      return { ...all, [key]: { ...prev, files: [...prev.files, file] } };
    });
  }

  removeFile(projectId: string, scope: string, uid: string): void {
    const key = this.fileKey(projectId, scope);
    this.uploadStates.update(all => {
      const prev = all[key] ?? this.emptyScope();
      return { ...all, [key]: { ...prev, files: prev.files.filter(f => f.uid !== uid) } };
    });
  }

  patchFile(projectId: string, scope: string, uid: string, patch: Partial<ResponseProjectUploadFile>): void {
    const key = this.fileKey(projectId, scope);
    this.uploadStates.update(all => {
      const prev = all[key] ?? this.emptyScope();
      return { ...all, [key]: { ...prev, files: prev.files.map(f => (f.uid === uid ? { ...f, ...patch } : f)) } };
    });
  }

  setUploadBatch(projectId: string, scope: string, total: number, categorizationDelta = total): void {
    const key = this.fileKey(projectId, scope);
    this.uploadStates.update(all => {
      const prev = all[key] ?? this.emptyScope();
      return {
        ...all,
        [key]: {
          ...prev,
          uploadTotal: prev.uploadTotal + total,
          uploadCompleted: 0,
          categorizationTotal: prev.categorizationTotal + categorizationDelta,
        },
      };
    });
  }

  incrementUploadCompleted(projectId: string, scope: string): void {
    const key = this.fileKey(projectId, scope);
    this.uploadStates.update(all => {
      const prev = all[key] ?? this.emptyScope();
      return { ...all, [key]: { ...prev, uploadCompleted: prev.uploadCompleted + 1 } };
    });
  }

  resetUploadBatch(projectId: string, scope: string): void {
    const key = this.fileKey(projectId, scope);
    this.uploadStates.update(all => {
      const prev = all[key] ?? this.emptyScope();
      return { ...all, [key]: { ...prev, uploadTotal: 0, uploadCompleted: 0, categorizationTotal: 0, categorizationCompleted: 0 } };
    });
  }

  decrementCategorizationTotal(projectId: string, scope: string): void {
    const key = this.fileKey(projectId, scope);
    this.uploadStates.update(all => {
      const prev = all[key] ?? this.emptyScope();
      return { ...all, [key]: { ...prev, categorizationTotal: Math.max(0, prev.categorizationTotal - 1) } };
    });
  }

  // ─── WS connection ────────────────────────────────────────────────────────

  connectToProcessing(): WsConnection {
    const messagesSubject = new Subject<ResponseProjectProcessingMessage>();
    const connectionStateSubject = new BehaviorSubject<'idle' | 'connecting' | 'open' | 'closed' | 'error'>('idle');
    let websocket: WebSocket | null = null;

    const url = (environment as Record<string, unknown>)['responseProjectApiGateway'] as string | undefined;
    if (!url) {
      console.error('[TendersResponsesService] responseProjectApiGateway URL not configured');
      connectionStateSubject.next('error');
    } else {
      connectionStateSubject.next('connecting');
      const ws = new WebSocket(url);
      websocket = ws;

      ws.addEventListener('open', () => {
        this.ngZone.run(() => {
          if (websocket !== ws) return;
          connectionStateSubject.next('open');
        });
      });

      ws.addEventListener('message', event => {
        this.ngZone.run(() => {
          if (websocket !== ws) return;
          try {
            const raw = JSON.parse(event.data);
            const message: ResponseProjectProcessingMessage = raw.message && raw.code != null ? raw.message : raw;
            messagesSubject.next(message);
          } catch {
            console.warn('[TendersResponsesService] Unparseable WS message', event.data);
          }
        });
      });

      ws.addEventListener('error', () => {
        this.ngZone.run(() => {
          if (websocket !== ws) return;
          connectionStateSubject.next('error');
        });
      });

      ws.addEventListener('close', () => {
        this.ngZone.run(() => {
          if (websocket !== ws) return;
          websocket = null;
          connectionStateSubject.next('closed');
        });
      });
    }

    return {
      connectionState$: connectionStateSubject.asObservable(),
      messages$: messagesSubject.asObservable(),
      sendPayload(payload: ResponseProjectProcessingPayload): void {
        if (websocket?.readyState === WebSocket.OPEN) {
          websocket.send(JSON.stringify(payload));
        } else {
          console.warn('[TendersResponsesService] WebSocket not open, cannot send');
        }
      },
      sendConflictResolution(message: ConflictResolutionMessage): void {
        if (websocket?.readyState === WebSocket.OPEN) {
          websocket.send(JSON.stringify(message));
        } else {
          console.warn('[TendersResponsesService] WebSocket not open, cannot resolve conflict');
        }
      },
      disconnect(): void {
        if (websocket) {
          websocket.close();
          websocket = null;
        }
      },
    };
  }

  startWsProcessing(
    conn: WsConnection,
    projectId: string,
    scope: string,
    payload?: ResponseProjectProcessingPayload,
    keepOpenWhilePending?: () => boolean
  ): void {
    const key = this.fileKey(projectId, scope);
    this.trackProcessingConn(key, conn);
    const allSubs = new Subscription();

    if (payload) {
      const stateSub = conn.connectionState$.subscribe(connState => {
        if (connState === 'open') {
          conn.sendPayload(payload);
          allSubs.remove(stateSub);
          stateSub.unsubscribe();
        }
        if (connState === 'error') {
          allSubs.unsubscribe();
          conn.disconnect();
        }
      });
      allSubs.add(stateSub);
    }

    allSubs.add(
      conn.messages$.subscribe(msg => {
        if (msg.status === 'renamed project' && msg.project_name !== DEFAULT_PROJECT_NAME) {
          this.updateProjectName(String(msg.project_id), msg.project_name);
        }

        if (msg.status === 'count') {
          this.uploadStates.update(all => {
            const prev = all[key] ?? this.emptyScope();
            const files = prev.files.map(f => (isArchiveFile(f.name) ? { ...f, hidden: true } : f));
            return { ...all, [key]: { ...prev, files, categorizationTotal: prev.categorizationTotal + msg.file_count } };
          });
        }

        if (msg.status === 'uploading') {
          this.uploadStates.update(all => {
            const prev = all[key] ?? this.emptyScope();
            const files = prev.files.some(f => f.uid === msg.uid)
              ? prev.files
              : [...prev.files, { uid: msg.uid, name: msg.filename, processingStep: DocumentProcessingStepEnum.UPLOADING }];
            return { ...all, [key]: { ...prev, files } };
          });
        }

        if (msg.status === 'uploaded') {
          this.uploadStates.update(all => {
            const prev = all[key] ?? this.emptyScope();
            let files = prev.files;
            if (files.some(f => f.uid === msg.uid)) {
              files = files.map(f =>
                f.uid === msg.uid ? { ...f, documentId: msg.id, size: msg.size, processingStep: DocumentProcessingStepEnum.UPLOADED } : f
              );
            } else {
              files = [
                ...files,
                {
                  uid: msg.uid,
                  name: msg.filename,
                  documentId: msg.id,
                  size: msg.size,
                  processingStep: DocumentProcessingStepEnum.UPLOADED,
                },
              ];
            }
            return { ...all, [key]: { ...prev, files } };
          });
        }

        if (msg.status === 'categorizing') {
          this.uploadStates.update(all => {
            const prev = all[key] ?? this.emptyScope();
            const files = prev.files.map(f => (f.uid === msg.uid ? { ...f, processingStep: DocumentProcessingStepEnum.CATEGORIZING } : f));
            return { ...all, [key]: { ...prev, files } };
          });
        }

        if (msg.status === 'categorized') {
          this.uploadStates.update(all => {
            const prev = all[key] ?? this.emptyScope();
            const files = prev.files.map(f =>
              f.uid === msg.uid
                ? {
                    ...f,
                    subtypeId: msg.subtype_id,
                    customName: msg.custom_name,
                    processingStep: DocumentProcessingStepEnum.CATEGORIZED,
                    justCategorized: true,
                  }
                : f
            );
            return { ...all, [key]: { ...prev, files, categorizationCompleted: prev.categorizationCompleted + 1 } };
          });
          this.ngZone.runOutsideAngular(() => {
            setTimeout(() => {
              this.ngZone.run(() => this.patchFile(projectId, scope, msg.uid, { justCategorized: false }));
            }, 2000);
          });
        }

        if (msg.status === 'error') {
          const state = this.uploadStates()[key] ?? this.emptyScope();
          const file =
            (msg.uid && state.files.find(f => f.uid === msg.uid)) ||
            (msg.filename && state.files.find(f => f.name === msg.filename)) ||
            null;
          if (file && file.processingStep === DocumentProcessingStepEnum.CATEGORIZING) {
            this.patchFile(projectId, scope, file.uid, { processingStep: DocumentProcessingStepEnum.UNCATEGORIZED });
          } else if (!msg.uid && !msg.filename && !msg.conflict_key) {
            // A global failure (not a per-file conflict) tears everything down.
            this.finalizeProcessing(key, conn, allSubs);
          }
        }

        if (msg.status === 'processing_complete') {
          if (keepOpenWhilePending?.()) {
            // Keep the socket open so conflicts can still be resolved; tear the
            // subscription down once the caller disconnects.
            allSubs.add(conn.connectionState$.subscribe(s => s === 'closed' && allSubs.unsubscribe()));
          } else {
            if (this.isLastProcessingConn(key, conn)) this.cleanupProcessedFiles(key);
            allSubs.unsubscribe();
            conn.disconnect();
          }
        }
      })
    );
  }

  private trackProcessingConn(key: string, conn: WsConnection): void {
    let conns = this.processingConns.get(key);
    if (!conns) {
      conns = new Set();
      this.processingConns.set(key, conns);
    }

    const nativeDisconnect = conn.disconnect.bind(conn);
    conn.disconnect = () => {
      conns!.delete(conn);
      if (conns!.size === 0) this.processingConns.delete(key);
      nativeDisconnect();
    };
    conns.add(conn);
  }

  private isLastProcessingConn(key: string, conn: WsConnection): boolean {
    const conns = this.processingConns.get(key);
    return !conns || (conns.size === 1 && conns.has(conn));
  }

  private cleanupProcessedFiles(key: string): void {
    this.uploadStates.update(all => {
      const prev = all[key] ?? this.emptyScope();
      const files = prev.files
        .filter(f => f.processingStep !== DocumentProcessingStepEnum.UPLOADING || f.documentId != null)
        .map(f => {
          if (
            f.processingStep === DocumentProcessingStepEnum.CATEGORIZING ||
            f.processingStep === DocumentProcessingStepEnum.UPLOADED ||
            f.processingStep === DocumentProcessingStepEnum.UPLOADING
          ) {
            return { ...f, processingStep: DocumentProcessingStepEnum.UNCATEGORIZED };
          }
          return f;
        })
        .filter(f => f.documentId != null);
      return {
        ...all,
        [key]: { files, uploadCompleted: 0, uploadTotal: 0, categorizationCompleted: 0, categorizationTotal: 0 },
      };
    });
  }

  private finalizeProcessing(key: string, conn: WsConnection, allSubs: Subscription): void {
    if (this.isLastProcessingConn(key, conn)) this.cleanupProcessedFiles(key);
    allSubs.unsubscribe();
    conn.disconnect();
  }

  private sortProjects(projects: ResponseProject[]): ResponseProject[] {
    const placeholder = projects.find(p => p.id === 'new');
    const rest = projects.filter(p => p.id !== 'new').sort((a, b) => a.name.localeCompare(b.name));
    return placeholder ? [...rest, placeholder] : rest;
  }

  setProjects(projects: ResponseProject[]): void {
    const placeholder = this.projects().find(p => p.id === 'new');
    const list = placeholder ? [...projects, placeholder] : projects;
    this.projects.set(this.sortProjects(list));
  }

  addNewProjectPlaceholder(): void {
    if (this.projects().some(p => p.id === 'new')) return;
    this.projects.update(current => [...current, { id: 'new', name: DEFAULT_PROJECT_NAME }]);
  }

  addProject(project: ResponseProject): void {
    this.projects.update(current => this.sortProjects([...current, project]));
  }

  updateProjectName(id: string, name: string): void {
    this.projects.update(current => this.sortProjects(current.map(p => (p.id === id ? { ...p, name } : p))));
  }

  markProjectClosed(id: string): void {
    this.projects.update(current => current.map(p => (p.id === id ? { ...p, is_active: false } : p)));
  }

  markProjectActive(id: string): void {
    this.projects.update(current => current.map(p => (p.id === id ? { ...p, is_active: true } : p)));
  }

  removeProject(id: string): void {
    this.projects.update(current => current.filter(p => p.id !== id));
  }
}
